import { useHandler } from "@redotech/react-util/hook";
import { DateSubconditionType } from "@redotech/redo-model/return-flow/date-subcondition";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoDateRangePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-range-picker";
import { RedoDateInput } from "@redotech/redo-web/arbiter-components/input/redo-date-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo } from "react";
import { DateSubconditionState } from "./date-subcondition";
interface DateSubconditionSelectorProps {
  state: DateSubconditionState;
  onChange: (updatedState: DateSubconditionState) => void;
  disabled?: boolean;
}

const dateSubconditionTypeToDisplayText: Record<DateSubconditionType, string> =
  {
    [DateSubconditionType.LAST_N_DAYS]: "Within X days",
    [DateSubconditionType.ON_OR_BEFORE]: "On or before",
    [DateSubconditionType.ON_OR_AFTER]: "On or after",
    [DateSubconditionType.BETWEEN]: "Between",
    [DateSubconditionType.ALL_TIME]: "All time",
  };

function dateToPlainDate(date: Date | null): Temporal.PlainDate | null {
  if (!date) {
    return null;
  }
  return Temporal.PlainDate.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
}

function plainDateToDate(plainDate: Temporal.PlainDate | null): Date | null {
  if (!plainDate) {
    return null;
  }
  return new Date(plainDate.year, plainDate.month - 1, plainDate.day);
}

const DATE_TYPE_OPTIONS: RadioGroupOption<DateSubconditionType>[] = [
  {
    id: DateSubconditionType.LAST_N_DAYS,
    label: dateSubconditionTypeToDisplayText[DateSubconditionType.LAST_N_DAYS],
  },
  {
    id: DateSubconditionType.ON_OR_BEFORE,
    label: dateSubconditionTypeToDisplayText[DateSubconditionType.ON_OR_BEFORE],
  },
  {
    id: DateSubconditionType.ON_OR_AFTER,
    label: dateSubconditionTypeToDisplayText[DateSubconditionType.ON_OR_AFTER],
  },
  {
    id: DateSubconditionType.BETWEEN,
    label: dateSubconditionTypeToDisplayText[DateSubconditionType.BETWEEN],
  },
];

export const DateSubconditionSelector = memo(function DateSubconditionSelector({
  state,
  onChange,
  disabled = false,
}: DateSubconditionSelectorProps) {
  // Helper function to update a specific field
  const updateField = useHandler(
    (field: keyof DateSubconditionState, value: any) => {
      onChange({ ...state, [field]: value });
    },
  );

  // Handle checkbox change (enable/disable time filtering)
  const handleTimeframeCheckboxChange = useHandler((checked: boolean) => {
    if (checked) {
      // When checkbox is checked, we need to set a non-ALL_TIME type
      const newType = DateSubconditionType.BETWEEN; // Default option
      let newState = { ...state, type: newType };

      // If this is a first-time selection with no data, set default values for BETWEEN
      const hasNoDateValues =
        !state.beforeDate &&
        !state.afterDate &&
        !state.startDate &&
        !state.endDate &&
        !state.days;

      if (hasNoDateValues && newType === DateSubconditionType.BETWEEN) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        newState = { ...newState, startDate, endDate };
      }

      onChange(newState);
    } else {
      // When unchecked, just change type to ALL_TIME but preserve all other values
      onChange({ ...state, type: DateSubconditionType.ALL_TIME });
    }
  });

  // Handle date type change
  const handleDateTypeChange = useHandler((type: DateSubconditionType) => {
    updateField("type", type);
  });

  // For date range
  const handleDateRangeChange = useHandler(
    (dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined) => {
      if (dateRange) {
        const [start, end] = dateRange;
        const startDate = plainDateToDate(start);
        const endDate = plainDateToDate(end);
        onChange({ ...state, startDate, endDate });
      } else {
        onChange({ ...state, startDate: null, endDate: null });
      }
    },
  );

  const temporalDateRange:
    | [Temporal.PlainDate, Temporal.PlainDate]
    | undefined = useMemo(() => {
    const start = dateToPlainDate(state.startDate);
    const end = dateToPlainDate(state.endDate);
    if (start && end) {
      return [start, end];
    }
    return undefined;
  }, [state.startDate, state.endDate]);

  // Check if time filtering is enabled (anything other than ALL_TIME)
  const isTimeFilteringEnabled = state.type !== DateSubconditionType.ALL_TIME;

  return (
    <Flex dir="column" gap="md">
      <RedoCheckbox
        disabled={disabled}
        label="Limit to timeframe"
        setValue={(value) =>
          handleTimeframeCheckboxChange(
            value === true || value === "indeterminate",
          )
        }
        size="xs"
        value={isTimeFilteringEnabled}
      />

      {/* Show date filtering options only when the checkbox is checked */}
      {isTimeFilteringEnabled && (
        <Flex dir="column" gap="md">
          <RedoRadioGroup
            disabled={disabled}
            options={DATE_TYPE_OPTIONS}
            setValue={handleDateTypeChange}
            size="xs"
            value={state.type}
          />

          {/* Date filter inputs based on selected type */}
          {state.type === DateSubconditionType.LAST_N_DAYS && (
            <Flex align="center" dir="row" gap="xs">
              <RedoTextInput
                inputTypeHint="number"
                label="Newer than (days)"
                min={1}
                setValue={(value) => {
                  const days = parseInt(value, 10);
                  if (!isNaN(days)) {
                    updateField("days", days);
                  }
                }}
                size="sm"
                state={disabled ? "disabled" : "default"}
                value={state.days?.toString() || "30"}
              />
            </Flex>
          )}

          {state.type === DateSubconditionType.ON_OR_BEFORE && (
            <RedoDateInput
              label="On or before date"
              setValue={(date) =>
                updateField("beforeDate", plainDateToDate(date))
              }
              size="sm"
              state={disabled ? "disabled" : "default"}
              value={dateToPlainDate(state.beforeDate)}
            />
          )}

          {state.type === DateSubconditionType.ON_OR_AFTER && (
            <RedoDateInput
              label="On or after date"
              setValue={(date) =>
                updateField("afterDate", plainDateToDate(date))
              }
              size="sm"
              state={disabled ? "disabled" : "default"}
              value={dateToPlainDate(state.afterDate)}
            />
          )}

          {state.type === DateSubconditionType.BETWEEN && (
            <div style={{ position: "relative", zIndex: 1 }}>
              <RedoDateRangePicker
                dateRange={temporalDateRange}
                dropdownStyle={{ zIndex: 9999 }}
                labelText="Date range"
                popperModifiers={[
                  { name: "offset", options: { offset: [0, -100] } },
                ]}
                setDateRange={handleDateRangeChange}
                size="sm"
              />
            </div>
          )}
        </Flex>
      )}
    </Flex>
  );
});
